import { Box } from '@web/components/atoms/Layout'
import {
  ComponentDirection,
  ProgressImageSubComponents,
  type ProgressImage as ProgressImageProps,
} from '@web/graphql-components'
import { SubComponentProps } from '../utils'
import { useEffect, useState } from 'react'
import { useMergeFields } from '../Text/MergeTags/MergeTag'

const ProgressImage = ({
  subComponents,
  SubComponent,
  id,
  type,
  mobileStyles,
  styles,
  progress,
  ...rest
}: ProgressImageProps & {
  progress: number
  SubComponent: React.FC<SubComponentProps>
}) => {
  const sc = subComponents as ProgressImageSubComponents
  const [imageProgress, setImageProgress] = useState(0)

  const direction = styles?.progressDirection || ComponentDirection.BottomToTop

  useEffect(() => {
    const timeout = setTimeout(() => {
      setImageProgress(progress)
    }, 100)

    return () => clearTimeout(timeout)
  }, [progress])

  const getMaskStyles = () => {
    switch (direction) {
      case ComponentDirection.TopToBottom:
        return {
          maskImage: 'linear-gradient(to bottom, transparent 0%, black 0%)',
          maskSize: `100% ${imageProgress}%`,
          maskPosition: 'top',
        }
      case ComponentDirection.LeftToRight:
        return {
          maskImage: 'linear-gradient(to right, transparent 0%, black 0%)',
          maskSize: `${imageProgress}% 100%`,
          maskPosition: 'left',
        }
      case ComponentDirection.RightToLeft:
        return {
          maskImage: 'linear-gradient(to left, transparent 0%, black 0%)',
          maskSize: `${imageProgress}% 100%`,
          maskPosition: 'right',
        }
      case ComponentDirection.BottomToTop:
      default:
        return {
          maskImage: 'linear-gradient(to top, transparent 0%, black 0%)',
          maskSize: `100% ${imageProgress}%`,
          maskPosition: 'bottom',
        }
    }
  }

  const maskStyles = getMaskStyles()

  const [currentMaskProgress, setCurrentMaskProgress] = useState(0)

  useEffect(() => {
    let animationFrame: number
    const startProgress = currentMaskProgress
    const targetProgress = imageProgress
    const duration = 3000 // match your transition duration
    const startTime = Date.now()

    const animate = () => {
      const elapsed = Date.now() - startTime
      const progress = Math.min(elapsed / duration, 1)

      // Ease-in-out interpolation
      const easedProgress = progress < 0.5 ? 2 * progress * progress : 1 - Math.pow(-2 * progress + 2, 2) / 2

      const newValue = startProgress + (targetProgress - startProgress) * easedProgress
      setCurrentMaskProgress(newValue)

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate)
      }
    }

    animationFrame = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(animationFrame)
  }, [imageProgress])

  useEffect(() => {
    useMergeFields.getState().setMergeField('ProgressImage.Percentage', `${currentMaskProgress.toFixed(0)}`)
  }, [currentMaskProgress])

  return (
    <Box position="relative" w="100%" display="flex" justifyContent={styles?.alignSelf || 'center'}>
      {sc.backgroundImage ? (
        <SubComponent {...rest} id={subComponents.backgroundImage} name="Background Image" parentId={id} />
      ) : (
        <Box filter="grayscale(1)">
          <SubComponent {...rest} id={sc.image} name="Background Image" parentId={id} />
        </Box>
      )}
      <Box
        w="100%"
        position="absolute"
        top={0}
        left={0}
        zIndex={1}
        transition="mask-size 3s ease-in-out"
        maskRepeat="no-repeat"
        style={maskStyles}
        display="flex"
        justifyContent={styles?.alignSelf || 'center'}
      >
        <SubComponent {...rest} id={sc.image} name="Image" parentId={id} />
      </Box>
      {styles.imageMaskRegion && (
        <Box
          w="100%"
          position="absolute"
          top={0}
          left={0}
          zIndex={2}
          maskRepeat="no-repeat"
          style={{
            maskImage: 'linear-gradient(black, black)',
            maskSize: `${styles.imageMaskRegion.w}% ${styles.imageMaskRegion.h}%`,
            // Convert to CSS positioning: position formula is (pos * 100) / (100 - size)
            maskPosition: `${
              styles.imageMaskRegion.w < 100 ? (styles.imageMaskRegion.x * 100) / (100 - styles.imageMaskRegion.w) : 0
            }% ${
              styles.imageMaskRegion.h < 100 ? (styles.imageMaskRegion.y * 100) / (100 - styles.imageMaskRegion.h) : 0
            }%`,
          }}
        >
          <SubComponent {...rest} id={sc.image} name="Image" parentId={id} />
        </Box>
      )}
      {sc.foregroundText && (
        <Box position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)" zIndex={3}>
          <SubComponent {...rest} id={sc.foregroundText} name="Progress Percentage" parentId={id} />
        </Box>
      )}
    </Box>
  )
}

export default ProgressImage
